import sdk, { resolveStoreRequestHeaders, storeUrl } from "@repo/api-sdk";

import {
  type ChatRequest,
  type ErrorBody,
  type ErrorCode,
  type ErrorPayload,
  type StatusResponse,
  type StreamEvent
} from "./contract";
import { readEvents } from "./stream";

const ERROR_CODES: ReadonlySet<string> = new Set<ErrorCode>([
  "limit_reached",
  "session_ended",
  "session_customer_mismatch",
  "token_expired",
  "busy",
  "disabled",
  "invalid_request",
  "internal"
]);

/**
 * A failed turn, reduced to what the drawer branches on. Carries the contract's
 * error code and its extra fields (`reset_at`, `scope`, `reason`, ...) but
 * never the service's `message` text: the drawer shows its own copy, and an
 * error that reaches an error reporter must not carry anything model-written.
 *
 * `network` is the one code the contract does not define: the request never got
 * an answer, or the stream ended without a terminal frame (section 3).
 */
export type AssistantFailure = {
  code: ErrorCode | "network";
  fields: Record<string, unknown>;
};

export class AssistantRequestError extends Error {
  readonly failure: AssistantFailure;

  constructor(failure: AssistantFailure) {
    // A fixed message, on purpose - see the type above.
    super(`Order assistant request failed (${failure.code})`);
    this.name = "AssistantRequestError";
    this.failure = failure;
  }
}

/** Reduce a contract error object to a failure, dropping `message`. */
export function failureFromPayload(payload: unknown): AssistantFailure {
  if (payload && typeof payload === "object") {
    const { code, ...fields } = payload as ErrorPayload;
    if (typeof code === "string" && ERROR_CODES.has(code)) {
      delete (fields as { message?: unknown }).message;
      return { code, fields };
    }
  }
  return { code: "internal", fields: {} };
}

/**
 * The agent is a plugin of the Medusa api: its routes are `/store/agent/*` on
 * the same backend the SDK talks to (contract section 2, as moved into the
 * api). They are store routes, so they need the publishable key like any other.
 */
function agentUrl(path: string): string {
  return storeUrl(`/store/agent${path}`);
}

/**
 * `GET /store/agent/status`: whether the entry button should be offered. A plain
 * JSON GET, so it goes through the SDK like any store call; any failure throws
 * and the caller reads that as "not available".
 */
export async function fetchAgentStatus(
  signal?: AbortSignal
): Promise<StatusResponse> {
  const body = await sdk.client.fetch<unknown>("/store/agent/status", {
    method: "GET",
    signal
  });
  return {
    enabled:
      typeof body === "object" &&
      body !== null &&
      (body as StatusResponse).enabled === true
  };
}

async function failureFromResponse(
  response: Response
): Promise<AssistantFailure> {
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    // Not JSON (a proxy's error page): fall through to the status.
  }
  // Any 401 is an expired sign-in, whatever the body: Medusa's own auth
  // middleware can answer before the agent route does, with its own JSON.
  if (response.status === 401) return { code: "token_expired", fields: {} };
  const error = (body as Partial<ErrorBody> | null)?.error;
  if (error) return failureFromPayload(error);
  return { code: "internal", fields: {} };
}

export type StreamChatParams = {
  message: string;
  /** The stored session id; absent starts a new conversation. */
  sessionId: string | null;
  signal?: AbortSignal;
};

/**
 * `POST /store/agent/chat`: run one turn and yield its events.
 *
 * Throws {@link AssistantRequestError} for a pre-stream failure (the JSON error
 * body) and for a request that never connected. A stream that ends or breaks
 * without `done`/`error` is the caller's to detect: it simply stops yielding.
 * An in-stream `error` frame is yielded like any other event.
 *
 * Auth is the customer JWT the Medusa js-sdk stores, read through its own
 * accessor; the publishable key and the Dev gate header come from the api
 * SDK's own header resolution (`resolveStoreRequestHeaders`).
 */
export async function* streamChat(
  params: StreamChatParams
): AsyncGenerator<StreamEvent> {
  const token = await sdk.client.getToken();
  if (!token) {
    throw new AssistantRequestError({ code: "token_expired", fields: {} });
  }

  const headers: Record<string, string> = {
    ...resolveStoreRequestHeaders(),
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json"
  };
  if (params.sessionId) headers["X-Session-Id"] = params.sessionId;

  const body: ChatRequest = { message: params.message };

  let response: Response;
  try {
    response = await fetch(agentUrl("/chat"), {
      method: "POST",
      headers,
      body: JSON.stringify(body),
      signal: params.signal
    });
  } catch (error) {
    if (params.signal?.aborted) throw error;
    throw new AssistantRequestError({ code: "network", fields: {} });
  }

  if (!response.ok || !response.body) {
    throw new AssistantRequestError(await failureFromResponse(response));
  }

  try {
    yield* readEvents(response.body);
  } catch (error) {
    if (params.signal?.aborted) throw error;
    // A dropped connection mid-stream. The caller treats a stream that ended
    // without a terminal frame as a network failure.
    return;
  }
}
